/**
 * Machine Capacity, in the existing right drawer (#483).
 *
 * The bottom Machines tab is the glance; this is the whole of one computer: what it is
 * currently under, the little history its own samples support, which Workers it is holding
 * and what they are running, why new work is or is not going to it, every limit the owner
 * has set on it, and the record of those limits being changed.
 *
 * Two rules the whole surface obeys. A reading the machine cannot take is Unknown, and
 * Unknown is drawn as Unknown - never as zero, never as a full meter, never as a reason to
 * refuse it work. And a preset is only ever its own exact numbers: they are shown before
 * anything is saved, and choosing Custom exposes the individual controls rather than
 * inventing values behind them.
 */
import { useState, type FormEvent, type ReactElement } from 'react';
import type { SupabaseClient } from '@supabase/supabase-js';
import {
  CAPACITY_PROFILE_ORDER, CEILING_OFF_WORD, CEILING_PERCENT_MAX, CEILING_PERCENT_MIN,
  RESERVE_PERCENT_MAX, RESERVE_PERCENT_MIN, WORKER_SEATS_MAX, WORKER_SEATS_MIN,
  capacityProfileBlurb, capacityProfileLabel, capacityProfileFields, capacityProfilePreview,
  ceilingText, checkCapacityLimits, isCapacityPreset,
  type CapacityGuardContract, type CapacityProfileKey, type MachineCapacity,
} from '@brunel/shared';
import type { Guard } from '../App';
import type { AuditEvent } from '../lib/data';
import { useNodeLimits } from '../lib/mutations';
import { MachineMeterBar } from './LiveDock';

/** What the owner has typed into the Custom controls. An empty box keeps what there is. */
interface CustomForm {
  readonly memory: string;
  readonly graphics: string;
  readonly cpuCeiling: string;
  readonly graphicsCeiling: string;
  readonly seats: string;
}

const EMPTY_FORM: CustomForm = { memory: '', graphics: '', cpuCeiling: '', graphicsCeiling: '', seats: '' };

function when(value: string | null): string {
  if (value === null) return 'Unknown';
  const at = new Date(value);
  return Number.isNaN(at.getTime()) ? 'Unknown' : at.toLocaleString();
}

function clock(value: string | null): string {
  if (value === null) return '—';
  const at = new Date(value);
  return Number.isNaN(at.getTime()) ? '—' : at.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

export function MachineCapacityPanel({
  client,
  guard,
  orgId,
  machines,
  selectedNodeId,
  onSelect,
  contract,
  limitChanges = [],
  onChanged,
}: {
  readonly client: SupabaseClient;
  readonly guard: Guard;
  readonly orgId: string;
  readonly machines: readonly MachineCapacity[];
  readonly selectedNodeId: string | null;
  readonly onSelect?: (nodeId: string) => void;
  readonly contract: CapacityGuardContract;
  readonly limitChanges?: readonly AuditEvent[];
  readonly onChanged?: () => void;
}): ReactElement {
  const { setLimits, busy, error } = useNodeLimits(client);
  const [pending, setPending] = useState<CapacityProfileKey | null>(null);
  const [form, setForm] = useState<CustomForm>(EMPTY_FORM);
  const [rejected, setRejected] = useState<string | null>(null);
  const [saved, setSaved] = useState<string | null>(null);

  const machine = machines.find((item) => item.nodeId === selectedNodeId) ?? machines[0] ?? null;

  if (machine === null) {
    return (
      <div className="stack machine-capacity" aria-label="Machine Capacity">
        <p className="muted">No computers are reporting yet. Nothing here is guessed while none are.</p>
      </div>
    );
  }

  const chosen = pending ?? machine.profile;
  const preview = capacityProfilePreview(chosen);

  // A preset is applied by its name; the database turns it into exactly the numbers shown
  // above the button. Custom sends only the boxes the owner actually filled in.
  const applyPreset = (key: CapacityProfileKey) => {
    const fields = capacityProfileFields(key);
    if (fields === null || !isCapacityPreset(key)) return;
    setRejected(null);
    setSaved(null);
    void guard('post_owner_command', async () => {
      if (await setLimits(orgId, machine.nodeId, { profile: key })) {
        setSaved(`${capacityProfileLabel(key)} saved on ${machine.name}.`);
        setPending(null);
        onChanged?.();
      }
    });
  };

  const applyCustom = (event: FormEvent) => {
    event.preventDefault();
    const checked = checkCapacityLimits(form);
    if (!checked.ok) { setRejected(checked.message); setSaved(null); return; }
    setRejected(null);
    setSaved(null);
    void guard('post_owner_command', async () => {
      if (await setLimits(orgId, machine.nodeId, checked.value)) {
        setSaved(`Saved on ${machine.name}.`);
        setForm(EMPTY_FORM);
        onChanged?.();
      }
    });
  };

  return (
    <div className="stack machine-capacity" aria-label="Machine Capacity" data-node={machine.nodeId}>
      {machines.length > 1 && (
        <div className="row machine-capacity-picker" role="group" aria-label="Computers">
          {machines.map((item) => (
            <button
              type="button"
              key={item.nodeId}
              className={item.nodeId === machine.nodeId ? 'chip on' : 'chip'}
              aria-pressed={item.nodeId === machine.nodeId}
              onClick={() => onSelect?.(item.nodeId)}
            >
              {item.name}
            </button>
          ))}
        </div>
      )}

      <header className="stack tight">
        <span className="row spread wrap">
          <strong>{machine.name}</strong>
          <span className="tag" data-presence={machine.presence}>{machine.presenceLabel}</span>
        </span>
        <small className="muted">{machine.versionLine} · last in touch {when(machine.lastSeenAt)}</small>
      </header>

      <section className="stack tight" aria-label="Current readings">
        <h3>What it is under now</h3>
        <span className="machine-meters wide">
          {machine.meters.map((meter) => <MachineMeterBar key={meter.key} meter={meter} />)}
        </span>
        <small className="muted">
          {[
            machine.cpuCount === null ? null : `${machine.cpuCount} processors`,
            machine.agentMemoryBytes === null
              ? null : `Brunel agent holding ${Math.round(machine.agentMemoryBytes / (1024 * 1024))} MB`,
            machine.activeRuns === null ? null : `${machine.activeRuns} run${machine.activeRuns === 1 ? '' : 's'} active`,
            machine.recentRuns === null
              ? 'recent work Unknown'
              : `${machine.recentRuns} finished in the last ${machine.recentWindowMinutes} minutes`,
          ].filter((part): part is string => part !== null).join(' · ')}
        </small>
        <small className="muted">
          Last resource reading: {when(machine.observedAt)}
          {machine.readingAgeLabel === null ? '' : ` (${machine.readingAgeLabel})`}
          {machine.stale ? ' — older than Brunel calls fresh, and still what the engine uses.' : ''}
        </small>
        {machine.localRuntimes.map((runtime) => (
          <small key={runtime.connectionKey} className="muted">{runtime.line}</small>
        ))}
      </section>

      <section className="stack tight" aria-label="Whether new work goes here">
        <h3>Taking new work: {machine.stanceLabel}</h3>
        <p className={`machine-stance-line tone-${machine.stance === 'accepting' ? 'ok' : machine.stance === 'unknown' ? 'unknown' : 'attention'}`}>
          {machine.dispatchLine}
        </p>
        {machine.nearingLine !== null && machine.stance !== 'held_back' && (
          <small className="muted">Nearing a limit: {machine.nearingLine}</small>
        )}
        {machine.guards.map((state) => (
          <small key={state.dimension} className="muted">{state.line}</small>
        ))}
      </section>

      {machine.history.length > 0 && (
        <section className="stack tight" aria-label="Recent readings">
          <h3>Recently</h3>
          <div className="machine-history" role="img"
            aria-label={`Recent readings for ${machine.name}, oldest first`}>
            {machine.history.map((point) => (
              <span key={point.at} className="machine-history-point" title={`${clock(point.at)} · ${[
                point.cpuPercent === null ? 'CPU Unknown' : `CPU ${point.cpuPercent}%`,
                point.memoryUsedPercent === null ? 'RAM Unknown' : `RAM ${point.memoryUsedPercent}%`,
                point.graphicsPercent === null ? 'GPU Unknown' : `GPU ${point.graphicsPercent}%`,
              ].join(' · ')}`}>
                <span className="machine-history-bar cpu"
                  data-known={point.cpuPercent === null ? 'false' : 'true'}
                  style={point.cpuPercent === null ? undefined : { height: `${point.cpuPercent}%` }} />
                <span className="machine-history-bar memory"
                  data-known={point.memoryUsedPercent === null ? 'false' : 'true'}
                  style={point.memoryUsedPercent === null ? undefined : { height: `${point.memoryUsedPercent}%` }} />
              </span>
            ))}
          </div>
          <small className="muted">
            Processor and memory, from this machine&apos;s own samples. A gap is a reading it could not take.
          </small>
        </section>
      )}

      <section className="stack tight" aria-label="Workers on this machine">
        <h3>Workers here</h3>
        {machine.seats.length === 0 && <p className="muted">No Worker seats are set up on this computer yet.</p>}
        {machine.seats.map((seat) => (
          <span key={seat.workerId} className="row spread machine-seat">
            <span>{seat.label}</span>
            <small className="muted">
              {seat.running
                ? `running${seat.taskTitle === null ? '' : `: ${seat.taskTitle}`}`
                : seat.status}
            </small>
          </span>
        ))}
      </section>

      {machine.holdBackEvents.length > 0 && (
        <section className="stack tight" aria-label="Recent hold-backs">
          <h3>When it was over a limit</h3>
          {machine.holdBackEvents.map((event) => (
            <small key={`${event.at}-${event.dimension}`} className="muted">
              {when(event.at)} — {event.detail}
            </small>
          ))}
          <small className="muted">Read back out of this machine&apos;s own samples, against the limits it carries now.</small>
        </section>
      )}

      <section className="stack tight" aria-label="Capacity limits">
        <h3>How hard Brunel may work it</h3>
        <p className="muted">
          These are the real numbers. A level is only a set of them, and it never changes this
          machine&apos;s Worker seat count.
        </p>
        <dl className="machine-limits">
          <div><dt>Worker seats</dt><dd>{machine.limits.workerSeats ?? 'Unknown'}</dd></div>
          <div><dt>Memory kept free</dt><dd>{machine.limits.reserveMemoryPercent}%</dd></div>
          <div><dt>Video memory kept free</dt><dd>{machine.limits.reserveGraphicsPercent}%</dd></div>
          <div><dt>Processor ceiling</dt><dd>{ceilingText(machine.limits.cpuCeilingPercent)}</dd></div>
          <div><dt>Graphics ceiling</dt><dd>{ceilingText(machine.limits.graphicsCeilingPercent)}</dd></div>
          <div><dt>Level</dt><dd>{capacityProfileLabel(machine.profile)}</dd></div>
        </dl>
        <small className="muted">
          A ceiling is judged on the average of this machine&apos;s readings over the last{' '}
          {Math.round(contract.windowSeconds / 6) / 10} minutes, needs at least {contract.minSamples} of
          them, lets go once the average falls {contract.releaseMarginPercent} points clear, and keeps
          each answer for at least {contract.cooldownSeconds} seconds.
        </small>

        <div className="row wrap machine-levels" role="group" aria-label="Capacity level">
          {CAPACITY_PROFILE_ORDER.map((key) => (
            <button
              type="button"
              key={key}
              className={chosen === key ? 'chip on' : 'chip'}
              aria-pressed={chosen === key}
              disabled={busy}
              onClick={() => { setPending(key); setRejected(null); setSaved(null); }}
            >
              {capacityProfileLabel(key)}
            </button>
          ))}
        </div>
        <small className="muted">{capacityProfileBlurb(chosen)}</small>

        {preview !== null && (
          <div className="stack tight machine-preview">
            <small data-testid="machine-profile-preview">{preview}</small>
            {chosen !== machine.profile && (
              <button type="button" className="primary" disabled={busy} onClick={() => applyPreset(chosen)}>
                Save {capacityProfileLabel(chosen)} on {machine.name}
              </button>
            )}
            {chosen === machine.profile && (
              <small className="muted">This machine is already on {capacityProfileLabel(chosen)}.</small>
            )}
          </div>
        )}

        {chosen === 'custom' && (
          <form className="stack tight machine-custom" onSubmit={applyCustom}>
            <label>
              <span>Worker seats ({WORKER_SEATS_MIN}-{WORKER_SEATS_MAX}; 0 means viewer/control only)</span>
              <input inputMode="numeric" maxLength={1} value={form.seats}
                aria-label={`Worker seats on ${machine.name}`}
                placeholder={`${machine.limits.workerSeats ?? WORKER_SEATS_MIN} now (empty: keep)`}
                onChange={(event) => setForm({ ...form, seats: event.target.value })} />
            </label>
            <label>
              <span>Memory kept free ({RESERVE_PERCENT_MIN}-{RESERVE_PERCENT_MAX}%)</span>
              <input inputMode="numeric" maxLength={2} value={form.memory}
                aria-label={`Memory to keep free on ${machine.name}`}
                placeholder={`${machine.limits.reserveMemoryPercent}% now (empty: keep)`}
                onChange={(event) => setForm({ ...form, memory: event.target.value })} />
            </label>
            <label>
              <span>Video memory kept free ({RESERVE_PERCENT_MIN}-{RESERVE_PERCENT_MAX}%)</span>
              <input inputMode="numeric" maxLength={2} value={form.graphics}
                aria-label={`Video memory to keep free on ${machine.name}`}
                placeholder={`${machine.limits.reserveGraphicsPercent}% now (empty: keep)`}
                onChange={(event) => setForm({ ...form, graphics: event.target.value })} />
            </label>
            <label>
              <span>Processor ceiling ({CEILING_PERCENT_MIN}-{CEILING_PERCENT_MAX}%, or {CEILING_OFF_WORD})</span>
              <input maxLength={3} value={form.cpuCeiling}
                aria-label={`Processor ceiling on ${machine.name}`}
                placeholder={`${ceilingText(machine.limits.cpuCeilingPercent)} now (empty: keep)`}
                onChange={(event) => setForm({ ...form, cpuCeiling: event.target.value })} />
            </label>
            <label>
              <span>Graphics ceiling ({CEILING_PERCENT_MIN}-{CEILING_PERCENT_MAX}%, or {CEILING_OFF_WORD})</span>
              <input maxLength={3} value={form.graphicsCeiling}
                aria-label={`Graphics ceiling on ${machine.name}`}
                placeholder={`${ceilingText(machine.limits.graphicsCeilingPercent)} now (empty: keep)`}
                onChange={(event) => setForm({ ...form, graphicsCeiling: event.target.value })} />
            </label>
            <button type="submit" className="primary" disabled={busy}>Save these numbers</button>
            <small className="muted">
              Only the boxes you fill in are sent. A ceiling of {CEILING_OFF_WORD} turns that guard off,
              which is not the same as a ceiling of zero.
            </small>
          </form>
        )}

        {rejected !== null && <p className="warn" role="alert">{rejected}</p>}
        {error !== null && <p className="warn" role="alert">{error}</p>}
        {saved !== null && <p className="muted" role="status">{saved}</p>}
      </section>

      {limitChanges.length > 0 && (
        <section className="stack tight" aria-label="Limit changes">
          <h3>Changes to these limits</h3>
          {limitChanges.map((event) => (
            <small key={event.id} className="muted">{when(event.occurred_at)} — {limitChangeLine(event)}</small>
          ))}
        </section>
      )}
    </div>
  );
}

/** One recorded limit change, said as what moved rather than as a payload dump. */
export function limitChangeLine(event: AuditEvent): string {
  const payload = (event.payload ?? {}) as {
    before?: Record<string, unknown>;
    after?: Record<string, unknown>;
  };
  const before = payload.before ?? {};
  const after = payload.after ?? {};
  const names: Readonly<Record<string, string>> = {
    workerSeats: 'Worker seats',
    reserveMemoryPercent: 'memory reserve',
    reserveGraphicsPercent: 'video memory reserve',
    cpuCeilingPercent: 'processor ceiling',
    graphicsCeilingPercent: 'graphics ceiling',
  };
  const moved: string[] = [];
  for (const [key, label] of Object.entries(names)) {
    const was = before[key] ?? null;
    const now = after[key] ?? null;
    if (was === now) continue;
    moved.push(`${label} ${was === null ? 'off' : was} → ${now === null ? 'off' : now}`);
  }
  const level = event.reason_code !== null && event.reason_code.startsWith('profile:')
    ? `${capacityProfileLabel(event.reason_code.slice('profile:'.length) as CapacityProfileKey)} chosen`
    : null;
  if (moved.length === 0) return level ?? 'no numeric limit changed';
  return level === null ? moved.join(', ') : `${level}: ${moved.join(', ')}`;
}
